import {
  Classes,
  InputGroupProps,
  PopoverPosition,
  PopoverProps,
} from "@blueprintjs/core";
import {
  ItemListPredicate,
  ItemListRendererProps,
  ItemRenderer,
  Select,
} from "@blueprintjs/select";
import { MULTISELECT_SELECT_ALL_OPTION, uuid } from "@hex/common";
import { filter } from "fuzzaldrin-plus";
import { first } from "lodash";
import React, {
  type JSX,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import styled from "styled-components";

import {
  AddIcon,
  SearchIcon,
  SingleChevronDownIcon,
  TickIcon,
} from "../components/icons/CustomIcons";
import { highlightFuzzyMatchText } from "../util/highlightFuzzyMatchText";
import { legacyToStandardRef } from "../util/refUtils";
import { TestSelectors } from "../util/testSelectors";
import { typedMemo } from "../util/typedMemo";

import { HexButton } from "./HexButton";
import { HexButtonProps } from "./HexButton.js";
import { BigMenuItemTextRenderer, HexMenu, HexMenuItem } from "./HexMenuItem";
import { HexTooltip } from "./HexTooltip.js";

// Stop the button from overflowing its contents when the text is too long,
// since we're wrapping it with a max-width container
export const HexSelectButton = styled(HexButton)`
  &.${Classes.BUTTON} > .${Classes.BUTTON_TEXT} {
    overflow: hidden;
  }
`;

const NoResults = styled.div<{ $small?: boolean; $lastElement?: boolean }>`
  padding-top: 5px;
  padding-bottom: ${({ $lastElement }) => ($lastElement ? 0 : 5)}px;

  font-size: ${({ $small, theme }) =>
    $small ? theme.fontSize.EXTRA_SMALL : theme.fontSize.SMALL};
  color: ${({ theme }) => theme.fontColor.MUTED};
  text-align: center;
`;

const HeaderDiv = styled.div<{ $small?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 5px;

  font-size: ${({ $small, theme }) =>
    $small ? theme.fontSize.EXTRA_SMALL : theme.fontSize.SMALL};
`;

const FooterDiv = styled.div<{ $small?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 5px;

  font-size: ${({ $small, theme }) =>
    $small ? theme.fontSize.EXTRA_SMALL : theme.fontSize.SMALL};

  border-top: 1px solid ${({ theme }) => theme.borderColor.MUTED};
`;

const HexMenuWrapper = styled.div<{ $filterable?: boolean }>`
  display: flex;
  flex-direction: column;
  padding-top: ${({ $filterable }) => ($filterable ? `5px` : "0px")};

  ${HexMenu} {
    flex: auto;
  }

  ${FooterDiv} {
    flex: none;
  }
`;

type HexItemListRendererProps<T> = {
  itemListRendererProps: ItemListRendererProps<T>;
  createItemFirst?: boolean;
  footerElement?: React.ReactNode;
  filterable?: boolean;
  headerElement?: React.ReactNode;
  initialContent?: JSX.Element;
  initialPageSize?: number;
  pageSize?: number;
  noResults: JSX.Element | null;
  small?: boolean;
};

// Borrows heavily from BP, except only with the features we care about
// See https://github.com/palantir/blueprint/blob/6be1a9d02984eea1c2db4142d86fca5cdf9957d0/packages/select/src/components/query-list/queryList.tsx#L345
export function HexItemListRenderer<T>({
  createItemFirst,
  filterable,
  footerElement,
  headerElement,
  initialContent,
  initialPageSize = 100,
  itemListRendererProps,
  noResults,
  pageSize = 50,
  small,
}: HexItemListRendererProps<T>): JSX.Element | null {
  const {
    filteredItems: items,
    itemsParentRef,
    menuProps,
    query,
    renderCreateItem,
    renderItem,
  } = itemListRendererProps;

  const isOnlySelectAll =
    items.length === 1 && items[0] === MULTISELECT_SELECT_ALL_OPTION;

  // Used to reference the parent component that InfiniteScroll uses to determine scroll position
  const menuRef = useRef(menuProps?.id ?? uuid());

  // Update the visible items when the filtered items change, keeping them in sync
  // e.g. when searching
  // We don't need to initialise the state of visible items, as the effect is called
  // on render
  const [visibleItems, setVisibleItems] = useState<T[]>([]);
  useEffect(() => {
    setVisibleItems(items.slice(0, initialPageSize));
  }, [initialPageSize, items, setVisibleItems]);

  const hasMore = useMemo(
    () => visibleItems.length < items.length,
    [items, visibleItems],
  );

  const next = useCallback(() => {
    setVisibleItems(items.slice(0, visibleItems.length + pageSize));
  }, [items, pageSize, setVisibleItems, visibleItems]);

  // omit noResults if createNewItemFromQuery and createNewItemRenderer are both supplied, and query is not empty
  const createItemView = renderCreateItem();
  const maybeNoResults = createItemView != null ? null : noResults;

  let menuContent: React.ReactNode = initialContent;

  const hasQuery = query.trim().length !== 0;
  // We don't check for `null` here as the caller might
  // want to set `null` explicitly
  const hasInitialContent = initialContent !== undefined;
  const renderItemsOrNoResults = hasQuery || !hasInitialContent;
  const renderItems =
    renderItemsOrNoResults && items.length > 0 && !isOnlySelectAll;

  if (renderItemsOrNoResults) {
    menuContent = renderItems ? visibleItems.map(renderItem) : maybeNoResults;
  }

  if (menuContent == null && createItemView == null) {
    return null;
  }

  return (
    <HexMenuWrapper
      $filterable={filterable}
      data-test={TestSelectors.HEX_MENU_WRAPPER}
    >
      {headerElement && <HeaderDiv $small={small}>{headerElement}</HeaderDiv>}
      <InfiniteScroll
        dataLength={visibleItems.length}
        hasMore={hasMore}
        loader={null}
        next={next}
        scrollableTarget={menuRef.current}
      >
        <StyledHexMenu
          id={menuRef.current}
          role="listbox"
          {...menuProps}
          ulRef={itemsParentRef}
        >
          {createItemFirst && createItemView}
          {menuContent}
          {!createItemFirst && createItemView}
        </StyledHexMenu>
      </InfiniteScroll>
      {footerElement && <FooterDiv $small={small}>{footerElement}</FooterDiv>}
    </HexMenuWrapper>
  );
}

const StyledHexMenu = styled(HexMenu)`
  &&& {
    margin: 0;
    padding: 0;
  }
`;

const StyledSmallHexMenuItem = styled(HexMenuItem)`
  min-height: 20px;
  padding-top: 3px;
  padding-bottom: 3px;
`;

interface GetSelectItemRendererOptions<T> {
  isActiveItem: (item: T) => boolean;
  isSelectedItem: (item: T) => boolean;
  small?: boolean;
  shouldDismissPopover?: boolean;
  renderItemLabelElement?: (
    item: T,
    state: { selected: boolean },
  ) => React.ReactNode;
}
export const getSelectItemRenderer = <T extends HexSelectItem>({
  isActiveItem,
  isSelectedItem,
  renderItemLabelElement = defaultRenderItemLabelElement,
  shouldDismissPopover,
  small,
}: GetSelectItemRendererOptions<T>): ItemRenderer<T> => {
  const MenuItemComponent = small ? StyledSmallHexMenuItem : HexMenuItem;

  return function SelectItemRenderer(item, { handleClick, modifiers, query }) {
    const contents = (
      <BigMenuItemTextRenderer
        active={modifiers.active}
        description={item.description}
        disabled={item.disabled}
        name={highlightFuzzyMatchText(item.title, query.trim())}
        small={small}
      />
    );

    return (
      <MenuItemComponent
        key={item.value}
        $keyboardActive={modifiers.active}
        active={isActiveItem(item)}
        disabled={item.disabled}
        icon={item.icon}
        labelElement={renderItemLabelElement(item, {
          selected: isSelectedItem(item),
        })}
        shouldDismissPopover={shouldDismissPopover}
        text={
          item.tooltipContent ? (
            <HexTooltip content={item.tooltipContent}>{contents}</HexTooltip>
          ) : (
            contents
          )
        }
        onClick={handleClick}
      />
    );
  };
};

const defaultRenderItemLabelElement = (
  _item: HexSelectItem,
  state: { selected: boolean },
): React.ReactNode | undefined => {
  return state.selected ? <TickIcon /> : undefined;
};

export interface HexSelectItem<TValue extends string = string> {
  /** The value that identifies the item. This needs to be unique amongst all items. */
  value: TValue;
  /** The display text for the item. */
  title: string;
  /** Optional extra details about the item. */
  description?: string;
  /** Optional icon for the item. */
  icon?: JSX.Element;
  /** By default, the dropdown will display the `title` of the selected item when closed.
   * However, you can provide a value here to show something different. */
  buttonTextOverride?: React.ReactNode;
  /** Whether or not the item should be disabled */
  disabled?: boolean;
  tooltipContent?: string;
  labelElement?: React.ReactNode;
}

export interface HexSelectProps<TValue extends string = string> {
  items: readonly HexSelectItem<TValue>[];
  value: TValue | null;
  onChange: (newValue: TValue, evt?: React.SyntheticEvent) => void;

  /** Optional JSX element that will be always visible at the bottom of the opened dropdown. */
  footerElement?: React.ReactNode;

  /** Optional JSX element that will be always visible at the top of the opened dropdown. */
  headerElement?: React.ReactNode;

  /** Optional JSX element rendered inside the menu when the current filter
   * query yields no items. Defaults to a muted "No matches found" message.
   * Pass `null` to suppress the no-results affordance entirely. */
  noResults?: JSX.Element | null;

  children?: React.ReactNode;
  className?: string;
  dataTest?: TestSelectors;
  disabled?: boolean;
  small?: boolean;
  extraSmall?: boolean;
  minimal?: boolean;
  fill?: boolean;
  captureDismiss?: boolean;
  filterable?: boolean;
  subtle?: boolean;
  /** "If true and the value provided doesn't match any of the given items,
  the HexSelect will show placeholderText. If false and the value provided
  doesn't match anything, then HexSelect will default to showing the first
  item in items. Defaults to true. **/
  autoSelectFirstItem?: boolean;
  placeholderText?: string;
  enableCreateNewItem?: boolean;
  itemRenderer?: ItemRenderer<HexSelectItem<TValue>>;
  buttonProps?: Partial<HexButtonProps>;
  popoverProps?: PopoverProps;
  /**
   * This gets passed to the `HexMenuItem` component
   */
  shouldDismissPopover?: boolean;
  renderItemLabelElement?: (
    item: HexSelectItem<TValue>,
    state: { selected: boolean },
  ) => React.ReactNode;
  /**
   * Called when the filter query changes.
   */
  onFilterQueryChange?: (query: string) => void;
  /**
   * Optional transformation applied to the user's query before filtering.
   */
  normalizeQueryForFilter?: (query: string) => string;
}

export const HexSelect = typedMemo(function HexSelect<
  TValue extends string = string,
>({
  autoSelectFirstItem = true,
  buttonProps,
  captureDismiss = false,
  children,
  className,
  dataTest,
  disabled,
  enableCreateNewItem = false,
  extraSmall,
  fill,
  filterable,
  footerElement,
  headerElement,
  itemRenderer,
  items,
  minimal,
  noResults: noResultsOverride,
  normalizeQueryForFilter,
  onChange,
  onFilterQueryChange,
  placeholderText,
  popoverProps: popoverPropsOverride,
  renderItemLabelElement,
  shouldDismissPopover = true,
  small,
  subtle,
  value,
}: HexSelectProps<TValue>) {
  const currentOptionOrUndefined = items.find((opt) => opt.value === value);

  // We should always have a selected option.
  // If we don't though, default to first option
  // unless "autoSelectFirstItem," is false.
  // This is very similar to what an HTML select does.
  const currentOption =
    currentOptionOrUndefined ?? (autoSelectFirstItem ? first(items) : null);

  // By controlling the active item, we ensure that our current value appears
  // selected when opening the Select for the first time or if the current value changes
  const [activeItemValue, setActiveItemValue] = useState<string | null>(value);
  const activeItem = items.find((opt) => opt.value === activeItemValue) ?? null;
  useEffect(() => {
    setActiveItemValue(value);
  }, [value]);
  const handleActiveItemChange = useCallback(
    (item: HexSelectItem<TValue> | null) => {
      setActiveItemValue(item?.value ?? null);
    },
    [],
  );

  const handleItemSelect = useCallback(
    (item: HexSelectItem<TValue>, evt?: React.SyntheticEvent) => {
      onChange(item.value, evt);
    },
    [onChange],
  );

  const filterItems: ItemListPredicate<HexSelectItem<TValue>> = useCallback(
    (query, items_) => {
      const trimmed = query.trim();
      const resolvedQuery =
        normalizeQueryForFilter != null
          ? normalizeQueryForFilter(trimmed)
          : trimmed;
      if (resolvedQuery.length === 0) {
        return items_;
      }

      // right now we only support searching the title,
      // we could make this customizable later if we want
      return filter(items_, resolvedQuery, {
        key: "title",
        usePathScoring: false,
      });
    },
    [normalizeQueryForFilter],
  );

  const createNewItemFromQuery = useCallback(
    (query: string): HexSelectItem<TValue> => ({
      // given that this query is user input, there's no great way to validate this
      // is a "value"; it just _is_ by nature of accepting user input
      value: query as TValue,
      title: query,
    }),
    [],
  );

  const createNewItemRenderer = useCallback(
    (
      query: string,
      active: boolean,
      handleClick: MouseEventHandler<HTMLElement>,
    ) => (
      <HexMenuItem
        key={query}
        active={active}
        icon={<AddIcon />}
        shouldDismissPopover={shouldDismissPopover}
        text={`Add ${query}`}
        onClick={handleClick}
      />
    ),
    [shouldDismissPopover],
  );

  const MenuRenderer = useCallback(
    function HexSelectItemListRenderer(
      props: ItemListRendererProps<HexSelectItem<TValue>>,
    ) {
      const noResults =
        noResultsOverride !== undefined ? (
          noResultsOverride
        ) : (
          <NoResults $lastElement={footerElement == null} $small={small}>
            No matches found
          </NoResults>
        );
      return (
        <HexItemListRenderer<HexSelectItem<TValue>>
          filterable={filterable ?? true}
          footerElement={footerElement}
          headerElement={headerElement}
          itemListRendererProps={props}
          noResults={noResults}
          small={small || extraSmall}
        />
      );
    },
    [
      extraSmall,
      filterable,
      footerElement,
      headerElement,
      noResultsOverride,
      small,
    ],
  );

  const MenuItemRenderer = useMemo(
    () =>
      getSelectItemRenderer({
        renderItemLabelElement,
        isActiveItem: (item) => activeItemValue === item.value,
        isSelectedItem: (item) => value === item.value,
        small: small || extraSmall,
        shouldDismissPopover,
      }),
    [
      small,
      extraSmall,
      shouldDismissPopover,
      activeItemValue,
      value,
      renderItemLabelElement,
    ],
  );

  const popoverProps: PopoverProps = useMemo(
    () => ({
      autoFocus: false,
      canEscapeKeyClose: true,
      minimal: true,
      hasBackdrop: true,
      position: PopoverPosition.BOTTOM_LEFT,
      captureDismiss,
      fill,
      rootBoundary: "viewport" as const,
      ...popoverPropsOverride,
    }),
    [captureDismiss, fill, popoverPropsOverride],
  );

  const inputProps: InputGroupProps = useMemo(
    () => ({
      small: small || extraSmall,
      leftIcon: <SearchIcon />,
    }),
    [small, extraSmall],
  );
  return (
    <Select<HexSelectItem<TValue>>
      activeItem={activeItem}
      className={className}
      createNewItemFromQuery={
        enableCreateNewItem ? createNewItemFromQuery : undefined
      }
      createNewItemRenderer={
        enableCreateNewItem ? createNewItemRenderer : undefined
      }
      disabled={disabled}
      filterable={filterable ?? true}
      inputProps={inputProps}
      itemListPredicate={filterItems}
      itemListRenderer={MenuRenderer}
      itemRenderer={itemRenderer ?? MenuItemRenderer}
      items={items as HexSelectItem<TValue>[]}
      popoverProps={popoverProps}
      onActiveItemChange={handleActiveItemChange}
      onItemSelect={handleItemSelect}
      onQueryChange={onFilterQueryChange}
    >
      {children ?? (
        <HexSelectButton
          {...buttonProps}
          ref={legacyToStandardRef(buttonProps?.ref)}
          alignText="left"
          data-test={dataTest}
          disabled={disabled}
          extraSmall={extraSmall}
          fill={fill}
          icon={currentOption?.icon ?? buttonProps?.icon}
          minimal={minimal}
          rightIcon={<SingleChevronDownIcon />}
          small={small}
          subtle={subtle}
          text={
            currentOption?.buttonTextOverride ??
            currentOption?.title ??
            placeholderText ??
            "Select..."
          }
        />
      )}
    </Select>
  );
});
